import {
  Component,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  DrilldownContainerChartSize,
  KpiTimeCycleChartData,
  TimeCycleCategory,
  TimeCycleLabels,
  TimeCycleTooltipInfo
} from '@models';
import { CommonFunctionsService, TimeCycleChartTooltipService } from '@services';
import { EChartsOption } from 'echarts';
import { CallbackDataParams } from 'echarts/types/dist/shared';

@Component({
  selector: 'time-cycle-chart',
  templateUrl: './time-cycle-chart.component.html',
  styleUrl: './time-cycle-chart.component.scss'
})
export class TimeCycleChartComponent implements OnInit, OnChanges {
  @ViewChild('chartWrapper') chartWrapper?: ElementRef<HTMLElement>;

  @Input() chartData!: KpiTimeCycleChartData;
  @Input() size!: DrilldownContainerChartSize;

  chartOption!: EChartsOption;
  showLegend!: boolean;
  legendLabels: TimeCycleCategory[] = [];
  private commonFunctionsService = inject(CommonFunctionsService);

  // tooltip state management
  hoveredTooltip: TimeCycleTooltipInfo | null = null;
  hoveredTooltipPosition = { x: 0, y: 0 };
  hoveredTooltipSource: 'bar' | 'yAxis' | null = null;
  private isBarHoverActive = false;
  private suppressTooltipFromOffset = false;
  private readonly tooltipWidth = 260;
  private readonly tooltipGap = 12;

  INIT_CHART = {
    renderer: 'svg'
  };

  categoryColorMap: Record<TimeCycleCategory, string | null> = {
    Ahead: '#01891E',
    Equal: '#005DAA',
    Behind: '#B60150',
    'Benchmark not available': '#A5A5AE',
    'actual date not available': null
  };

  categoryLabelMap: Partial<Record<TimeCycleCategory, string>> = {
    Ahead: 'Ahead',
    Equal: 'Equal',
    Behind: 'Behind',
    'Benchmark not available': 'Unavailable'
  };

  private textColor = '#1B1B1D';

  private legendCategories: TimeCycleCategory[] = [
    'Ahead',
    'Equal',
    'Behind',
    'Benchmark not available'
  ];

  private ngZone: NgZone = inject(NgZone);
  private timeCycleChartTooltipService: TimeCycleChartTooltipService = inject(
    TimeCycleChartTooltipService
  );

  ngOnInit() {
    this.refreshChartState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartData'] || changes['size']) {
      this.refreshChartState();
    }
  }

  onChartInit(chart: any) {
    this.commonFunctionsService.resizeChartWhenContainerIsReady(chart);
    if (this.size === 'small') {
      this.bindChartHoverEvents(chart);
    }
  }

  private bindChartHoverEvents(chart: any) {
    if (typeof chart.off === 'function') {
      chart.off('mouseover');
      chart.off('mousemove');
      chart.off('mouseout');
      chart.off('globalout');
    }

    if (typeof chart.on !== 'function') {
      return;
    }

    // Bar hover
    chart.on('mouseover', { seriesName: 'Time weeks' }, (params: any) => {
      this.ngZone.run(() => this.handleBarMouseOver(params));
    });
    chart.on('mousemove', { seriesName: 'Time weeks' }, (params: any) => {
      this.ngZone.run(() => this.handleBarMouseOver(params));
    });
    chart.on('mouseover', { seriesName: 'Offset' }, () => {
      this.ngZone.run(() => this.handleOffsetHover());
    });
    chart.on('mousemove', { seriesName: 'Offset' }, () => {
      this.ngZone.run(() => this.handleOffsetHover());
    });
    chart.on('mouseout', { seriesName: 'Offset' }, () => {
      this.ngZone.run(() => {
        this.suppressTooltipFromOffset = false;
        this.clearTooltip();
      });
    });

    // Y-axis hover
    chart.on('mouseover', { componentType: 'yAxis' }, (params: any) => {
      this.ngZone.run(() => this.handleYAxisMouseOver(params));
    });
    chart.on('mousemove', { componentType: 'yAxis' }, (params: any) => {
      this.ngZone.run(() => this.handleYAxisMouseOver(params));
    });

    // Hide cases
    chart.on('mouseout', { seriesName: 'Time weeks' }, (params: any) => {
      this.ngZone.run(() => this.handleBarMouseOut(params));
    });
    chart.on('mouseout', { componentType: 'yAxis' }, () => {
      this.ngZone.run(() => this.clearTooltip());
    });
    chart.on('globalout', () => {
      this.ngZone.run(() => {
        this.suppressTooltipFromOffset = false;
        this.clearTooltip();
      });
    });
  }

  private refreshChartState(): void {
    if (!this.chartData || !this.size) {
      return;
    }

    this.showLegend = this.getShowLegend();
    this.legendLabels = this.showLegend ? this.getLegendCategories() : [];
    this.chartOption = this.getChartOption();
  }

  getChartContainerHeight(): string {
    return this.size === 'small' ? '150px' : '100%';
  }

  private getShowLegend(): boolean {
    return this.chartData.category.some((cat) => this.legendCategories.includes(cat));
  }

  private getLegendCategories(): TimeCycleCategory[] {
    if (!this.chartData.category.includes('Benchmark not available')) {
      return this.legendCategories.filter((cat) => cat != 'Benchmark not available');
    }

    return this.legendCategories;
  }

  private getChartOption(): EChartsOption {
    return {
      grid: {
        left: 0,
        right: 0,
        top: this.getGridTop(),
        bottom: this.getGridBottom()
      },
      xAxis: {
        type: 'value',
        splitLine: { show: this.showXAxisSplitLine() },
        axisLine: { show: true },
        splitNumber: 4,
        name: 'Weeks',
        nameLocation: 'middle',
        nameTextStyle: {
          fontSize: 12,
          padding: [15, 40, 0, 0],
          color: this.textColor
        },
        axisLabel: {
          color: this.textColor
        },
        max: (value) => {
          return this.getXAxisMaxValue(value.max);
        }
      },
      yAxis: {
        type: 'category',
        triggerEvent: true,
        splitLine: { show: false },
        data: this.getYAxisLabels(),
        axisLabel: {
          color: this.textColor
        }
      },

      series: [
        {
          name: 'Offset',
          type: 'bar',
          stack: 'Total',
          itemStyle: {
            borderColor: 'transparent',
            color: 'transparent'
          },
          emphasis: {
            itemStyle: {
              borderColor: 'transparent',
              color: 'transparent'
            }
          },
          data: this.chartData.offset.toReversed()
        },
        {
          name: 'Time weeks',
          type: 'bar',
          stack: 'Total',
          barWidth: '60%',
          label: {
            show: true,
            position: 'right',
            formatter: (params) => {
              return this.getBarLabels(params);
            }
          },
          data: this.getTimeWeeksDataObjects()
        }
      ]
    };
  }

  private showXAxisSplitLine(): boolean {
    return this.size === 'medium';
  }

  private getYAxisLabels(): string[] {
    return this.chartData.labels.toReversed().map((labels: TimeCycleLabels) => {
      if (this.size === 'small') {
        return labels.small;
      } else {
        return labels.large;
      }
    });
  }

  private getGridTop(): number {
    return this.size === 'small' ? 8 : 16;
  }

  private getGridBottom(): number {
    return this.size === 'small' ? 20 : 60;
  }

  private getXAxisMaxValue(maxVal: number): number {
    const intPart = Math.trunc(Math.abs(maxVal));
    const zeroCount = intPart === 0 ? 0 : Math.floor(Math.log10(intPart));
    const tenBase = 10 ** zeroCount;
    const roundedMaxVal = Math.ceil(maxVal / tenBase) * tenBase;
    const axisOffset = Math.ceil(roundedMaxVal / 4);

    return roundedMaxVal + axisOffset;
  }

  private getTimeWeeksDataObjects(): any[] {
    const reversedCategory = this.chartData.category.toReversed();

    return this.chartData.timeWeeks.toReversed().map((time, idx) => {
      return { value: time, itemStyle: { color: this.categoryColorMap[reversedCategory[idx]] } };
    });
  }

  private getBarLabels(params: CallbackDataParams): string {
    if (params.value === 0) return '';

    const reversedCategory = this.chartData.category.toReversed();
    const stringValue = `${params.value}`;
    return this.size === 'small'
      ? stringValue
      : stringValue + ` | ${this.categoryLabelMap[reversedCategory[params.dataIndex]]}*`;
  }

  private handleBarMouseOver(params: any): void {
    if (this.size !== 'small' || Array.isArray(params) || params?.seriesName !== 'Time weeks') {
      return;
    }

    this.suppressTooltipFromOffset = false;

    const tooltipInfo = this.timeCycleChartTooltipService.getTooltipInfo(
      this.chartData,
      this.size,
      params?.name,
      params?.dataIndex
    );
    if (
      !tooltipInfo ||
      tooltipInfo.status === 'actual date not available' ||
      typeof tooltipInfo.weeks !== 'number'
    ) {
      this.isBarHoverActive = false;
      this.hoveredTooltip = null;
      return;
    }

    this.isBarHoverActive = true;
    this.hoveredTooltip = tooltipInfo;
    this.setTooltipPosition(params);
  }

  private handleYAxisMouseOver(params: any): void {
    if (this.size !== 'small' || Array.isArray(params)) {
      return;
    }

    if (this.suppressTooltipFromOffset) {
      return;
    }

    // Avoid y-axis events overriding tooltip position while a bar is actively hovered.
    if (this.isBarHoverActive) {
      return;
    }

    const label = params?.value ?? params?.name;
    const tooltipInfo = this.timeCycleChartTooltipService.getTooltipInfo(
      this.chartData,
      this.size,
      label,
      params?.dataIndex
    );
    if (!tooltipInfo) {
      return;
    }

    this.hoveredTooltip = tooltipInfo;
    this.setTooltipPosition(params, 'yAxis');
  }

  private handleBarMouseOut(params: any): void {
    if (this.size !== 'small' || !params || params.seriesName === 'Time weeks') {
      this.clearTooltip();
    }
  }

  private clearTooltip(): void {
    this.isBarHoverActive = false;
    this.hoveredTooltip = null;
    this.hoveredTooltipSource = null;
  }

  private handleOffsetHover(): void {
    this.suppressTooltipFromOffset = true;
    this.clearTooltip();
  }

  private setTooltipPosition(params: any, source: 'bar' | 'yAxis' = 'bar'): void {
    const { pointerX, pointerY } = this.timeCycleChartTooltipService.getPointerCoordinates(
      params,
      this.chartWrapper?.nativeElement
    );
    const xPosition = this.timeCycleChartTooltipService.getTooltipXPosition(
      pointerX,
      source,
      this.tooltipWidth,
      this.tooltipGap,
      globalThis.innerWidth ?? 0
    );

    this.hoveredTooltipSource = source;
    this.hoveredTooltipPosition = {
      x: xPosition,
      y: Math.max(pointerY, 0)
    };
  }
}
